import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  TrendingUp, Flame, ChevronDown, X,
  MessageCircle, Sparkles, Repeat, Hash
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  NR01: {
    scene: '🍕 피자를 나눠 먹으며 소수에 대해 이야기하는 민준이와 소연이.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '피자 3조각을 4명이 나누면 한 명이 몇 조각을 먹어? 3÷4 = 0.75잖아. 이게 유한소수야?' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '소수점 아래가 0.75로 딱 끝나니까 유한소수인 것 같은데, 맞아?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 소수점 아래 자릿수가 유한한(끝이 있는) 소수를 유한소수라고 해요. 3/4 = 0.75처럼요.' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '그럼 1÷3 = 0.333...은요? 끝이 없잖아요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '소수점 아래가 끝없이 계속되는 소수를 무한소수라고 해요. 1/3 = 0.333...처럼 특정 숫자가 반복되는 건 순환소수라고 하죠.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '소수의 세계: 유한소수(끝남) vs 무한소수(무한) — 무한소수 중 규칙이 있으면 순환소수!' },
    ],
    reveal: '유한소수: 소수점 아래가 유한 (예: 0.75) | 무한소수: 소수점 아래가 무한 (예: 0.333...) | 순환소수: 무한소수 중 반복 패턴이 있는 것',
    revealEmoji: '🔢',
  },
  NR02: {
    scene: '🔄 수학 동아리에서 순환소수의 점 표기법을 배우는 날.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, 0.333...을 쓸 때마다 점을 계속 찍는 건 너무 불편해요. 더 쉬운 방법이 없나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '좋은 질문! 순환소수는 반복되는 부분 첫 숫자와 끝 숫자 위에 점을 찍어서 표현해요. 0.333...은 0.3̇이라고 써요.' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '그럼 0.142857142857...처럼 6자리가 반복되면요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '반복 구간의 첫 숫자 1 위와 마지막 숫자 7 위에 점을 찍어요: 0.1̇4285̇7 이렇게요!' },
      { speaker: 'student', emoji: '😄', name: '민준', text: '와, 훨씬 간단하네요! 순환마디가 뭐예요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '반복되는 숫자의 한 묶음을 순환마디라고 해요. 0.333...의 순환마디는 3, 0.142857142857...의 순환마디는 142857이에요.' },
    ],
    reveal: '순환소수 표기: 반복 시작·끝 숫자 위에 점 | 0.333... = 0.3̇ | 0.1212... = 0.1̇2̇ | 순환마디 = 반복되는 한 묶음',
    revealEmoji: '🔄',
  },
  NR03: {
    scene: '📐 민준이가 순환소수를 분수로 바꾸는 방법을 고민하고 있다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, 0.333...이 1/3이라는 건 알겠는데, 0.636363...은 어떤 분수예요? 이건 어떻게 계산해요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: 'x = 0.636363...으로 놓고, 순환마디가 2자리니까 양변에 100을 곱해요. 100x = 63.636363...' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '그러면 100x - x = 63.636363... - 0.636363... = 63이 되네요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! 99x = 63이므로 x = 63/99 = 7/11이에요. 순환마디 자릿수만큼 10의 거듭제곱을 곱하는 게 핵심이에요.' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '순환마디 1자리면 10배, 2자리면 100배, 3자리면 1000배를 곱하면 되겠네요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '순환소수 → 분수: x로 놓기 → 순환마디 자릿수만큼 곱하기 → 빼기 → 나누기. 4단계 공식!' },
    ],
    reveal: 'x = 순환소수 → 10ⁿx (n=순환마디 자릿수) → 빼기로 순환 제거 → 분수로 표현 | 0.63̇ = 63/99 = 7/11',
    revealEmoji: '📐',
  },
  NR04: {
    scene: '🔬 과학 시간에 분수와 소수의 관계를 탐구하는 수업.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '오늘은 어떤 분수가 유한소수가 되는지, 순환소수가 되는지 알아볼게요. 먼저 1/4을 소수로 나타내면?' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '1÷4 = 0.25. 유한소수예요! 분모가 4 = 2²이라서?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 기약분수의 분모가 2와 5의 거듭제곱만으로 이루어지면 유한소수가 돼요. 1/6은요?' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '1/6에서 분모 6 = 2×3이에요. 3이 있으니까 유한소수가 아니겠네요. 0.1666...이니까 순환소수!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽해요! 분모에 2, 5 이외의 소인수가 있으면 반드시 순환소수가 됩니다. 이게 유리수와 소수의 핵심 관계예요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '유리수 ↔ 소수 변환의 핵심: 기약분수 분모의 소인수 구성이 모든 것을 결정합니다!' },
    ],
    reveal: '기약분수 분모의 소인수가 2, 5뿐 → 유한소수 | 2, 5 이외의 소인수 포함 → 순환소수 | 모든 유리수는 유한소수 또는 순환소수!',
    revealEmoji: '🔬',
  },
  NR05: {
    scene: '🏪 편의점에서 할인 계산을 하다가 소수가 등장했다.',
    lines: [
      { speaker: 'friend', emoji: '😄', name: '소연', text: '민준아, 이 과자가 3개에 1000원이래. 한 개에 얼마야?' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '1000 ÷ 3 = 333.333...원이야. 근데 이걸 분수로 어떻게 표현하지?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '(수업 중) 순환소수와 분수를 자유롭게 변환할 수 있으면 이런 상황도 쉽게 해결돼요.' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '그럼 순환소수가 모두 분수로 나타낼 수 있다는 게 신기해요. 규칙이 없는 무한소수는 분수가 안 된다고요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 순환소수는 반드시 분수(유리수)로 표현 가능해요. 반면 π나 √2 같은 무리수는 순환하지 않는 무한소수예요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '유리수 = 유한소수 또는 순환소수. 무리수 = 순환하지 않는 무한소수. 이 구분이 중2 수학의 핵심!' },
    ],
    reveal: '유리수 = 유한소수 or 순환소수 | 무리수 = 순환 없는 무한소수 | 순환소수 ↔ 분수 변환 자유자재!',
    revealEmoji: '🏪',
  },
  NR06: {
    scene: '😰 순환소수가 어렵다는 친구를 위한 처음부터 차근차근 설명.',
    lines: [
      { speaker: 'friend', emoji: '😰', name: '다른 친구', text: '나는 순환소수가 뭔지도 모르겠어. 분수로 바꾸는 건 더 어렵고. 수학 포기하고 싶어...' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '나도 처음엔 그랬어! 그냥 소수점 아래가 반복되는 숫자라고 생각해. 0.333...은 3이 계속 반복되잖아.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '아주 좋은 설명이에요! 핵심만 기억하면 돼요. x = 0.3̇으로 놓고, 10x = 3.3̇, 10x - x = 3, 9x = 3, x = 1/3.' },
      { speaker: 'friend', emoji: '😮', name: '다른 친구', text: '오... 이렇게 빼면 소수점이 사라지는구나! 순환 부분을 없애는 게 핵심이었네.' },
      { speaker: 'student', emoji: '😄', name: '민준', text: '맞아! 두 식의 소수 부분이 똑같아서 빼면 깔끔해지는 거야. 신기하지?' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '포기하지 마세요! 순환소수는 "x로 놓고, 곱하고, 빼면" 반드시 분수가 됩니다. 단 3단계예요.' },
    ],
    reveal: '순환소수 → 분수: ① x = 순환소수 ② 순환마디 자릿수만큼 10ⁿ 곱하기 ③ 빼서 순환 제거 ④ 분수 완성!',
    revealEmoji: '💪',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'NR01',
    title: '유한소수와 무한소수',
    subtitle: '소수를 유한과 무한으로 분류한다',
    icon: '🔢',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['소수 분류', '유한소수', '무한소수'],
    objective: '소수를 유한소수와 무한소수로 분류하고, 순환소수의 개념을 이해한다.',
    concept: `**유한소수**: 소수점 아래 숫자가 유한개인 소수
예) 0.5, 0.75, 0.125

**무한소수**: 소수점 아래 숫자가 무한히 계속되는 소수
예) 0.333..., 0.142857142857...

**순환소수**: 무한소수 중 소수점 아래의 어떤 자리에서부터 일정한 숫자의 배열이 한없이 반복되는 소수
예) 0.333... (3이 반복), 0.142857142857... (142857이 반복)

**비순환 무한소수**: 반복 패턴이 없는 무한소수 (무리수)
예) π = 3.14159265... (반복 없음)`,
    keyPoints: [
      '유한소수는 소수점 아래가 끝이 있다 → 분수로 쉽게 표현 가능',
      '순환소수는 반복 패턴이 있는 무한소수 → 분수(유리수)로 표현 가능',
      '비순환 무한소수(π, √2 등)는 분수로 나타낼 수 없는 무리수',
      '모든 유리수는 유한소수 또는 순환소수로 나타낼 수 있다',
    ],
    examples: [
      {
        question: '다음을 유한소수, 순환소수, 비순환 무한소수로 분류하시오: 0.6 / 0.272727... / π',
        answer: '0.6 → 유한소수 (끝이 있음)\n0.272727... → 순환소수 (27이 반복)\nπ = 3.14159... → 비순환 무한소수 (무리수)',
        explanation: '소수점 아래가 끝나는지, 반복되는지, 둘 다 아닌지 확인합니다.',
      },
      {
        question: '1/8을 소수로 나타내고 유한·무한 여부를 판단하시오.',
        answer: '1÷8 = 0.125 → 유한소수',
        explanation: '분모 8 = 2³이므로 2의 거듭제곱만 → 유한소수가 됩니다.',
      },
    ],
    practice: {
      question: '다음 분수를 소수로 나타내고 유한소수인지 순환소수인지 판단하시오: 5/6',
      answer: '5÷6 = 0.8333... = 0.83̇ → 순환소수',
      hint: '5÷6을 직접 나눠보세요. 끝이 없으면 순환 여부를 확인하세요.',
      steps: ['5÷6 = 0.8333...', '소수점 아래가 무한히 계속됨', '3이 반복됨 → 순환소수', '순환마디: 3'],
    },
    misconceptions: ['NM01', 'NM02'],
  },
  {
    id: 'NR02',
    title: '순환소수의 표현',
    subtitle: '순환마디를 이해하고 점 표기법으로 나타낸다',
    icon: '🔄',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    textColor: 'text-purple-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['순환마디', '점 표기법', '순환소수 표현'],
    objective: '순환마디를 파악하고 점 표기법으로 순환소수를 간결하게 표현한다.',
    concept: `**순환마디**: 순환소수에서 반복되는 숫자의 한 묶음

**점 표기법**: 순환마디의 양 끝 숫자 위에 점을 찍어 표현
• 순환마디가 1자리: 그 숫자 위에 점 1개
• 순환마디가 2자리 이상: 첫 숫자와 끝 숫자 위에 점

**예시**:
• 0.333... → 순환마디 3 → 0.3̇
• 0.272727... → 순환마디 27 → 0.2̇7̇
• 0.123123... → 순환마디 123 → 0.1̇23̇
• 3.145145... → 순환마디 145 → 3.1̇45̇`,
    keyPoints: [
      '순환마디: 반복되는 숫자 묶음의 최소 단위',
      '점 표기: 순환마디 첫 자리와 끝 자리에 점',
      '순환마디가 1자리면 그 위에 점 하나만',
      '순환하지 않는 부분(소수점 바로 뒤)은 점 없이 그대로',
    ],
    examples: [
      {
        question: '0.5121212...를 점 표기법으로 나타내시오.',
        answer: '0.51̇2̇',
        explanation: '5는 반복 안 됨, 12가 반복됨. 순환마디 12 → 1 위와 2 위에 점.',
      },
      {
        question: '순환마디를 구하시오: 2.3456456456...',
        answer: '순환마디: 456, 점 표기: 2.34̇56̇',
        explanation: '앞의 3은 반복 안 됨, 456이 반복. 4와 6 위에 점.',
      },
    ],
    practice: {
      question: '0.714285714285...를 점 표기법으로 나타내고 순환마디를 쓰시오.',
      answer: '순환마디: 714285, 점 표기: 0.7̇1428̇5',
      hint: '반복되는 부분을 찾아보세요. 처음부터 6자리가 반복됩니다.',
      steps: ['714285가 반복됨을 확인', '순환마디 = 714285 (6자리)', '첫 자리 7 위에 점, 끝 자리 5 위에 점', '0.7̇1428̇5'],
    },
    misconceptions: ['NM03', 'NM04'],
  },
  {
    id: 'NR03',
    title: '순환소수를 분수로 변환',
    subtitle: '미지수 x를 이용한 변환 공식을 마스터한다',
    icon: '📐',
    color: 'bg-green-500',
    lightColor: 'bg-green-50',
    borderColor: 'border-green-300',
    textColor: 'text-green-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['순환소수→분수', '미지수 활용', '변환 공식'],
    objective: 'x를 이용한 방정식으로 순환소수를 정확하게 분수로 변환한다.',
    concept: `**변환 4단계 공식**:
① x = 순환소수 로 놓는다
② 순환마디의 자릿수(n)만큼 10ⁿ을 곱한다
③ (10ⁿ·x) - x 로 소수 부분을 제거한다
④ x = (정수)/(10ⁿ - 1) 로 분수 완성

**예시 1**: x = 0.3̇
• 10x = 3.333...
• 10x - x = 3 → 9x = 3 → x = 3/9 = **1/3**

**예시 2**: x = 0.2̇7̇ (= 0.272727...)
• 100x = 27.2727...
• 100x - x = 27 → 99x = 27 → x = 27/99 = **3/11**

**예시 3**: x = 0.13̇ (= 0.1333... 소수 부분 일부만 순환)
• 10x = 1.333..., 100x = 13.333...
• 100x - 10x = 12 → 90x = 12 → x = 12/90 = **2/15**`,
    keyPoints: [
      '순환마디 n자리 → 10ⁿ 곱하기',
      '두 식을 빼면 소수점 아래 순환 부분이 사라진다',
      '순환 시작 전 자리가 있으면 10ᵐ도 곱해서 처리',
      '최약분수로 기약화 필수',
    ],
    examples: [
      {
        question: '0.6̇을 분수로 나타내시오.',
        answer: 'x = 0.666...\n10x = 6.666...\n10x - x = 6 → 9x = 6 → x = 6/9 = 2/3',
        explanation: '순환마디 1자리이므로 10배. 빼면 9x = 6. 기약분수: 2/3.',
      },
      {
        question: '0.1̇8̇을 분수로 나타내시오.',
        answer: 'x = 0.181818...\n100x = 18.1818...\n99x = 18 → x = 18/99 = 2/11',
        explanation: '순환마디 18 (2자리) → 100배. 99x = 18. 기약: 2/11.',
      },
    ],
    practice: {
      question: '0.4̇2̇를 분수로 나타내시오.',
      answer: 'x = 0.424242...\n100x = 42.4242...\n99x = 42 → x = 42/99 = 14/33',
      hint: '순환마디가 42(2자리)이므로 100을 곱하세요.',
      steps: ['x = 0.424242...로 놓기', '100x = 42.4242...', '100x - x = 42 → 99x = 42', 'x = 42/99 = 14/33 (기약분수)'],
    },
    misconceptions: ['NM05', 'NM06'],
  },
  {
    id: 'NR04',
    title: '유리수와 소수의 관계',
    subtitle: '분모의 소인수로 유한소수 여부를 판단한다',
    icon: '🔬',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    textColor: 'text-orange-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['유한소수 판별', '소인수 분석', '유리수↔소수'],
    objective: '기약분수의 분모를 소인수분해하여 유한소수/순환소수 여부를 판단한다.',
    concept: `**판별 규칙**:
기약분수 a/b를 소수로 나타낼 때
• b의 소인수가 2와 5뿐 → **유한소수**
• b에 2, 5 이외의 소인수 포함 → **순환소수**

**예시**:
• 3/8 → 8 = 2³ → 소인수 2뿐 → **유한소수** (0.375)
• 7/20 → 20 = 2²×5 → 소인수 2, 5뿐 → **유한소수** (0.35)
• 5/6 → 6 = 2×3 → 소인수에 3 포함 → **순환소수** (0.8333...)
• 2/7 → 7은 소수 (7 포함) → **순환소수** (0.285714...)

**기약분수화 먼저!**:
• 4/6 → 기약: 2/3 → 3 포함 → 순환소수
• 6/15 → 기약: 2/5 → 소인수 2, 5뿐 → 유한소수`,
    keyPoints: [
      '반드시 기약분수로 만든 후 분모를 소인수분해',
      '분모 소인수 = {2, 5}뿐이면 유한소수',
      '분모 소인수에 3, 7, 11 등이 있으면 순환소수',
      '유한소수가 되려면 분모를 10의 거듭제곱 꼴로 만들 수 있어야 함',
    ],
    examples: [
      {
        question: '다음 분수가 유한소수인지 순환소수인지 판별하시오: 9/24',
        answer: '9/24 → 기약: 3/8 → 8 = 2³ → 소인수 2뿐 → 유한소수',
        explanation: '먼저 기약분수(3/8)로 만들고, 분모 8 = 2³. 소인수 2뿐이므로 유한소수.',
      },
      {
        question: '14/35를 판별하시오.',
        answer: '14/35 → 기약: 2/5 → 5는 2 또는 5 → 유한소수 (0.4)',
        explanation: '기약분수 2/5. 분모 5는 소인수 5뿐. 유한소수.',
      },
    ],
    practice: {
      question: '다음 중 유한소수가 되는 분수를 모두 고르시오: 3/7, 5/12, 7/25, 11/40',
      answer: '7/25 (25 = 5²), 11/40 (40 = 2³×5) → 유한소수\n3/7 (7 포함), 5/12 (기약 5/12, 12=2²×3 → 3 포함) → 순환소수',
      hint: '각 분수를 기약분수로 만든 후 분모를 소인수분해하세요.',
      steps: ['3/7: 분모 7 → 2,5 외 소인수 → 순환소수', '5/12: 기약 5/12, 12=4×3 → 3 포함 → 순환소수', '7/25: 25=5² → 유한소수', '11/40: 40=8×5=2³×5 → 유한소수'],
    },
    misconceptions: ['NM07', 'NM08'],
  },
  {
    id: 'NR05',
    title: '오답 분석과 개념 정리',
    subtitle: '순환소수 단원 전체 오개념을 점검한다',
    icon: '🔍',
    color: 'bg-red-500',
    lightColor: 'bg-red-50',
    borderColor: 'border-red-300',
    textColor: 'text-red-700',
    difficulty: '심화',
    difficultyColor: 'bg-red-100 text-red-700',
    tags: ['오개념 정리', '오답 분석', '단원 총정리'],
    objective: '순환소수 단원의 핵심 오개념을 파악하고 올바른 개념으로 교정한다.',
    concept: `**단원 핵심 오개념 목록**:

NM01 — 순환소수도 무한소수의 일종
• 오류: "순환소수는 유한소수다"
• 교정: 순환소수는 무한소수! 단지 규칙(반복)이 있을 뿐

NM02 — 비순환 무한소수는 유리수가 아님
• 오류: "0.101001000...도 분수로 나타낼 수 있다"
• 교정: 반복 패턴이 없는 무한소수는 무리수 → 분수 불가

NM03 — 순환마디 표기 오류
• 오류: 0.1̇23̇을 0.123̇으로 표기
• 교정: 순환마디의 첫 숫자와 끝 숫자에 각각 점

NM05 — 분수→순환소수 변환 시 뺄 식 선택 오류
• 오류: 0.13̇에서 100x - x를 바로 계산
• 교정: 10x = 1.333..., 100x = 13.333... → 100x - 10x = 12

NM07 — 기약분수화 생략 후 소인수 판별
• 오류: 4/6의 분모 6 = 2×3 → 순환소수라고 판단
• 교정: 먼저 기약분수 2/3으로 → 분모 3 → 순환소수 (결과는 같지만 반드시 기약분수 먼저!)`,
    keyPoints: [
      '순환소수 ≠ 유한소수. 반드시 무한소수 범주 안에 있음',
      '점 표기: 순환마디 첫 번째와 마지막 자리에 점',
      '분수 변환 시 순환 시작 위치에 따라 곱하는 수가 다름',
      '유한소수 판별: 기약분수화 → 분모 소인수 확인',
    ],
    examples: [
      {
        question: '0.2̇3̇을 분수로 나타내시오. (순환마디: 23)',
        answer: 'x = 0.232323...\n100x = 23.2323...\n99x = 23 → x = 23/99',
        explanation: '순환마디 2자리(23) → 100배. 99x = 23.',
      },
      {
        question: '12/45가 유한소수인지 판별하시오.',
        answer: '기약: 4/15 → 15 = 3×5 → 3 포함 → 순환소수',
        explanation: '기약분수 4/15. 분모 15 = 3×5. 3이 있으므로 순환소수.',
      },
    ],
    practice: {
      question: '0.31̇4̇를 분수로 나타내시오. (순환마디: 14, 3은 순환하지 않음)',
      answer: 'x = 0.31414...\n10x = 3.1414...\n1000x = 314.1414...\n1000x - 10x = 311 → 990x = 311 → x = 311/990',
      hint: '3은 순환하지 않으므로 10x와 1000x를 이용하세요.',
      steps: ['x = 0.31414...', '10x = 3.1414..., 1000x = 314.1414...', '1000x - 10x = 311 → 990x = 311', 'x = 311/990 (더 이상 약분 안 됨)'],
    },
    misconceptions: ['NM05', 'NM06', 'NM07', 'NM08'],
  },
  {
    id: 'NR06',
    title: '수포자를 위한 처음부터',
    subtitle: '순환소수가 왜 어렵게 느껴지는지 이해한다',
    icon: '💪',
    color: 'bg-pink-500',
    lightColor: 'bg-pink-50',
    borderColor: 'border-pink-300',
    textColor: 'text-pink-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['기초부터', '자신감', '개념 정립'],
    objective: '순환소수의 핵심 아이디어를 쉬운 언어로 이해하고 자신감을 회복한다.',
    concept: `**순환소수가 어렵게 느껴지는 이유**:
① 점 표기가 낯설다 (0.3̇이 뭔지 모름)
② 분수 변환이 마법처럼 보인다
③ 기약분수, 소인수분해가 혼합되어 복잡함

**쉽게 생각하는 법**:
• 순환소수 = "반복되는 소수"
• 0.3̇ = "3이 계속 반복돼요"
• 0.1̇2̇ = "12가 계속 반복돼요"

**분수 변환 쉬운 공식**:
• 순환마디 1자리: 분자 = 순환마디, 분모 = 9
  → 0.3̇ = 3/9 = 1/3
• 순환마디 2자리: 분자 = 순환마디, 분모 = 99
  → 0.2̇7̇ = 27/99 = 3/11
• 순환마디 3자리: 분모 = 999
  → 0.1̇2̇3̇ = 123/999 = 41/333

**유한소수 판별 쉬운 공식**:
→ 분모를 소인수분해해서 2와 5만 있으면 OK!`,
    keyPoints: [
      '순환소수 = 반복되는 무한소수 (간단한 핵심!)',
      '분수 변환: 순환마디 n자리 → 분모에 9가 n개',
      '순환 전 자리가 있으면 분모 9×10^m 형태',
      '겁먹지 마세요! 패턴만 익히면 누구나 할 수 있어요',
    ],
    examples: [
      {
        question: '0.4̇를 간단히 분수로 나타내시오.',
        answer: '순환마디 4 (1자리) → 분모 9 → 4/9',
        explanation: '순환마디 1자리일 때: 순환마디/9. 4/9는 이미 기약분수.',
      },
      {
        question: '0.5̇4̇를 간단히 분수로 나타내시오.',
        answer: '순환마디 54 (2자리) → 분모 99 → 54/99 = 6/11',
        explanation: '순환마디 2자리: 54/99. 기약분수: 54÷9=6, 99÷9=11 → 6/11.',
      },
    ],
    practice: {
      question: '0.8̇을 분수로 나타내고 기약분수로 만드시오.',
      answer: '순환마디 8 (1자리) → 8/9 (이미 기약분수)',
      hint: '순환마디 1자리: 순환마디/9. 8과 9의 공약수를 확인하세요.',
      steps: ['순환마디 = 8 (1자리)', '분모 = 9', '8/9', '8과 9의 GCD = 1 → 이미 기약분수'],
    },
    misconceptions: ['NM01', 'NM08'],
  },
]

const TOTAL_LEARN_STEPS = STEPS.length
const UNIT_ID = 'N2-1'

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────
function PracticeBox({ practice, stepId }: { practice: typeof STEPS[0]['practice'], stepId: string }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-indigo-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 30)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story, stepId }: { story: StepStory, stepId: string }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-purple-100 text-purple-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-slate-200 rounded-full">
          <div className="h-1 bg-indigo-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      <div className="flex gap-2">
        {lineIdx < story.lines.length - 1 ? (
          <button onClick={() => setLineIdx(i => i + 1)}
            className="flex-1 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <ChevronRight size={13} /> 다음
          </button>
        ) : (
          <button onClick={() => setRevealed(true)}
            className="flex-1 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <Sparkles size={13} /> 핵심 개념 보기
          </button>
        )}
        <button onClick={() => { setLineIdx(0); setRevealed(false) }}
          className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-500 hover:bg-slate-50 transition-colors">
          처음
        </button>
      </div>
      {revealed && (
        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-2">
          <span className="text-xl">{story.revealEmoji}</span>
          <p className="text-xs text-indigo-800 font-medium leading-relaxed">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id))
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-amber-500" />
        <p className="text-sm font-bold text-amber-800">AI 예상 파편</p>
        <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold">
          {visible.length}개
        </span>
      </div>
      <div className="space-y-2">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-start gap-2 p-2 rounded-xl border ${cfg.color}`}>
              <span className="text-base">{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{f.fragment.misconceptionLabel}</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0">
                <X size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
interface N3LearnPageProps {
  defaultStepId?: string
}

export default function N3LearnPage({ defaultStepId }: N3LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(
    defaultStepId ? (STEPS.find(s => s.id === defaultStepId) ?? STEPS[0]) : STEPS[0]
  )
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const learnProgress = useLearnProgress(UNIT_ID)
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) setSelectedStep(step)
    }
  }, [defaultStepId])

  useEffect(() => {
    if (selectedStep) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  }, [selectedStep?.id])

  const isStepCompleted = (stepId: string) =>
    learnProgress?.steps?.[stepId]?.status === 'completed' ||
    learnProgress?.steps?.[stepId]?.status === 'mastered'

  const isStepPracticed = (stepId: string) =>
    learnProgress?.steps?.[stepId]?.practiceAnswered === true

  const completedCount = STEPS.filter(s => isStepCompleted(s.id)).length

  const handleComplete = (stepId: string) => {
    completeStep(UNIT_ID, stepId)
    const idx = STEPS.findIndex(s => s.id === stepId)
    if (idx < STEPS.length - 1) setSelectedStep(STEPS[idx + 1])
  }

  return (
    <div className="p-4 space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <Repeat size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              유리수와 순환소수
              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">N2-1</span>
            </h1>
            <p className="text-sm text-slate-500">순환소수의 표현과 분수 변환을 마스터한다</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-xs text-slate-500">학습 진도</div>
            <div className="text-sm font-bold text-teal-600">{completedCount}/{TOTAL_LEARN_STEPS} 완료</div>
          </div>
          <div className="w-16 h-16 relative">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#0d9488" strokeWidth="3"
                strokeDasharray={`${(completedCount / TOTAL_LEARN_STEPS) * 100} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-teal-600">
                {Math.round((completedCount / TOTAL_LEARN_STEPS) * 100)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 배너 */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-cyan-700 text-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <Repeat size={16} />
          <span className="font-bold text-sm">순환소수 핵심 개념 로드맵</span>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {['유한·무한소수', '순환마디·점 표기', '분수 변환(x공식)', '유한소수 판별', '오개념 정리', '처음부터'].map((t, i) => (
            <span key={i} className="bg-white/20 px-2 py-1 rounded-lg font-medium">
              NR0{i + 1}: {t}
            </span>
          ))}
        </div>
      </div>

      {/* 3열 레이아웃 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 단계 목록 */}
        <div className="space-y-2">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide px-1">학습 단계</p>
          {STEPS.map((step, idx) => {
            const completed = isStepCompleted(step.id)
            const practiced = isStepPracticed(step.id)
            const active = selectedStep?.id === step.id
            const video = getApprovedByTag(step.id)
            return (
              <button key={step.id} onClick={() => setSelectedStep(step)}
                className={`w-full text-left p-3 rounded-2xl border transition-all flex items-start gap-3 ${
                  active ? 'border-teal-400 bg-teal-50 shadow-sm' :
                  completed ? 'border-emerald-200 bg-emerald-50' :
                  'border-slate-200 bg-white hover:bg-slate-50'
                }`}>
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-sm ${
                  completed ? 'bg-emerald-500' : active ? step.color : 'bg-slate-200'
                }`}>
                  {completed ? <CheckCircle size={16} className="text-white" /> : <span>{step.icon}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className={`text-xs font-mono font-bold ${active ? 'text-teal-600' : 'text-slate-400'}`}>
                      {step.id}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${step.difficultyColor}`}>
                      {step.difficulty}
                    </span>
                    {video.length > 0 && <Youtube size={10} className="text-red-400" />}
                  </div>
                  <p className={`text-sm font-semibold truncate ${active ? 'text-teal-700' : 'text-slate-700'}`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-slate-400 truncate">{step.subtitle}</p>
                  {practiced && (
                    <span className="text-xs text-indigo-500 font-medium">✏️ 연습 완료</span>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* 파편 패널 */}
        <div className="hidden lg:block">
          <PredictedFragmentPanel fragments={predictedFragments} />
        </div>

        {/* 단계 상세 */}
        <div className="lg:col-span-1 space-y-4">
          {selectedStep ? (
            <>
              {/* 단계 헤더 */}
              <div className={`rounded-2xl ${selectedStep.lightColor} border ${selectedStep.borderColor} p-4`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{selectedStep.icon}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-mono font-bold ${selectedStep.textColor}`}>{selectedStep.id}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${selectedStep.difficultyColor}`}>
                        {selectedStep.difficulty}
                      </span>
                    </div>
                    <h2 className="text-base font-bold text-slate-800">{selectedStep.title}</h2>
                    <p className="text-xs text-slate-500">{selectedStep.subtitle}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {selectedStep.tags.map(t => (
                    <span key={t} className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedStep.lightColor} ${selectedStep.textColor} border ${selectedStep.borderColor}`}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* 학습 목표 */}
              <div className="card">
                <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
                  <Target size={12} className="text-teal-500" /> 학습 목표
                </p>
                <p className="text-sm text-slate-700">{selectedStep.objective}</p>
              </div>

              {/* 스토리텔링 */}
              {STEP_STORIES[selectedStep.id] && (
                <div>
                  <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
                    <MessageCircle size={12} className="text-indigo-500" /> 개념 스토리
                  </p>
                  <StoryCard story={STEP_STORIES[selectedStep.id]} stepId={selectedStep.id} />
                </div>
              )}

              {/* 개념 설명 */}
              <div className="card">
                <p className="text-xs font-bold text-slate-500 mb-3 flex items-center gap-1">
                  <BookOpen size={12} className="text-blue-500" /> 개념 설명
                </p>
                <div className="prose prose-sm max-w-none">
                  {selectedStep.concept.split('\n').map((line, i) => (
                    <p key={i} className={`text-sm ${line.startsWith('**') ? 'font-bold text-slate-800' : 'text-slate-600'} ${line.startsWith('•') ? 'ml-2' : ''} leading-relaxed mb-1`}>
                      {line.replace(/\*\*/g, '')}
                    </p>
                  ))}
                </div>
              </div>

              {/* 핵심 포인트 */}
              <div className="card bg-amber-50 border border-amber-200">
                <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
                  <Star size={12} /> 핵심 포인트
                </p>
                <ul className="space-y-1.5">
                  {selectedStep.keyPoints.map((kp, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                      <span className="text-amber-400 font-bold flex-shrink-0">✦</span>
                      <span>{kp}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 예제 */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 flex items-center gap-1">
                  <FileText size={12} className="text-purple-500" /> 예제 문제
                </p>
                {selectedStep.examples.map((ex, i) => (
                  <ExampleCard key={i} example={ex} idx={i} />
                ))}
              </div>

              {/* 연습 문제 */}
              <PracticeBox practice={selectedStep.practice} stepId={selectedStep.id} />

              {/* 개념 영상 */}
              <div className="card bg-red-50 border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <Youtube size={14} className="text-red-500" />
                  <p className="text-sm font-bold text-red-700">개념 영상</p>
                </div>
                {(() => {
                  const approved = getApprovedByTag(selectedStep.id)
                  return approved.length > 0 ? (
                    <button onClick={() => setVideoModal({ stepId: selectedStep.id, label: selectedStep.title })}
                      className="w-full flex items-center gap-2 py-2 px-3 bg-red-100 hover:bg-red-200 rounded-xl transition-colors">
                      <Play size={14} className="text-red-600" />
                      <span className="text-sm text-red-700 font-medium">{approved[0].title}</span>
                    </button>
                  ) : (
                    <div className="text-xs text-slate-400 text-center py-3">
                      영상 준비 중 — 크리에이터 모집 중
                    </div>
                  )
                })()}
              </div>

              {/* 오개념 경고 */}
              {selectedStep.misconceptions.length > 0 && (
                <div className="card bg-orange-50 border border-orange-200">
                  <p className="text-xs font-bold text-orange-700 mb-2 flex items-center gap-1">
                    <AlertTriangle size={12} /> 주의! 자주 틀리는 오개념
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedStep.misconceptions.map(m => (
                      <span key={m} className="text-xs font-mono bg-orange-100 text-orange-700 px-2 py-1 rounded-lg font-bold">
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 완료 버튼 */}
              <div className="flex gap-2">
                {!isStepCompleted(selectedStep.id) ? (
                  <button onClick={() => handleComplete(selectedStep.id)}
                    className="flex-1 py-3 rounded-2xl bg-teal-500 hover:bg-teal-600 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2">
                    <CheckCircle size={16} /> 학습 완료
                  </button>
                ) : (
                  <div className="flex-1 py-3 rounded-2xl bg-emerald-100 text-emerald-700 font-bold text-sm flex items-center justify-center gap-2">
                    <CheckCircle size={16} /> 완료됨
                  </div>
                )}
                <button
                  onClick={() => {
                    const idx = STEPS.findIndex(s => s.id === selectedStep.id)
                    if (idx < STEPS.length - 1) setSelectedStep(STEPS[idx + 1])
                  }}
                  className="px-4 py-3 rounded-2xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors flex items-center gap-1 text-sm">
                  다음 <ArrowRight size={14} />
                </button>
              </div>
            </>
          ) : (
            <div className="card flex flex-col items-center justify-center h-60 text-center">
              <Repeat size={40} className="text-slate-200 mb-3" />
              <p className="text-slate-400 text-sm">왼쪽에서 학습 단계를 선택하세요</p>
            </div>
          )}
        </div>
      </div>

      {/* 영상 모달 */}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
